import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { faDownload, faPlus } from "@fortawesome/free-solid-svg-icons";
import { lastValueFrom, Subscription } from "rxjs";
import { finalize } from "rxjs/operators";
import LocalizationConstants from "src/app/constants/localization-constants";
import RouteNameConstants from "src/app/constants/route-name-constants";
import { SubscriptionType } from "src/app/models/Enums";
import { Tool } from "src/app/models/Tool";
import { ToolDetails } from "src/app/models/ToolDetails";
import { ToolUpdate } from "src/app/models/ToolUpdate";
import { AuthService } from "src/app/services/auth.service";
import { BrandingService } from "src/app/services/branding.service";
import { CompanyService } from "src/app/services/company.service";
import { GlobalOverlayService } from "src/app/services/global-overlay.service";
import { LocalizationService } from "src/app/services/localization.service";
import { ModalService } from "src/app/services/modal.service";

import { ToolService } from "../../services/tool.service";
import { ConfirmationDialogData } from "../common/confirmation-dialog/confirmation-dialog-modal.component";

@Component({
  selector: "app-tool-details",
  templateUrl: "./tool-details.component.html",
  styleUrls: ["./tool-details.component.scss"],
  standalone: false,
})
export class ToolDetailsComponent implements OnDestroy, OnInit {
  readonly routeNameConstants = RouteNameConstants;
  readonly localizationConstants = LocalizationConstants;
  private _subscriptions$: Subscription[] = [];

  faPlus = faPlus;
  faDownload = faDownload;
  companyId: string;
  serialNumber: string;
  tool: Tool;
  toolDetails: ToolDetails;
  canToggleActiveStatus: boolean;

  constructor(
    private route: ActivatedRoute,
    private globalOverlayService: GlobalOverlayService,
    private toolService: ToolService,
    private companyService: CompanyService,
    private router: Router,
    private modalService: ModalService,
    private localizationService: LocalizationService,
    private brandingService: BrandingService,
    private authService: AuthService,
  ) {
    this.companyId = this.route.snapshot.paramMap.get("company");

    this.serialNumber = this.route.snapshot.paramMap.get("serialnumber");
    if (!this.companyId || !this.serialNumber) return;

    this.getToolDetails(this.companyId, this.serialNumber);

    this._subscriptions$.push(this.onCompanyChange());
  }

  async ngOnInit(): Promise<void> {
    this.canToggleActiveStatus = await this.authService.hasSubscriptionAsync(
      SubscriptionType.EditTool,
    );
  }

  onCompanyChange() {
    return this.companyService.companyChange.subscribe(() => {
      this.router.navigate([RouteNameConstants.TOOLS]);
    });
  }

  getToolDetails(companyId: string, serialNumber: string) {
    this.globalOverlayService.show();
    this.toolService
      .getToolDetails(companyId, serialNumber)
      .pipe(finalize(() => this.globalOverlayService.close()))
      .subscribe((response) => {
        if (response) {
          this.toolDetails = response;
          this.tool = response.details;
        }
      });
  }

  async updateToolActive(isActive: boolean): Promise<void> {
    this.globalOverlayService.show();

    const canDeactivateTool = await this.canDeactivateToolAsync(isActive);
    if (!canDeactivateTool) {
      return;
    }
    this.toolService
      .updateActive(this.companyId, this.serialNumber, {
        ...new ToolUpdate(),
        isActive: isActive,
      })
      .pipe(finalize(() => this.globalOverlayService.close()))
      .subscribe(() => (this.tool.isActive = isActive));
  }

  private async canDeactivateToolAsync(isActive: boolean): Promise<boolean> {
    // if activating tool, no need to validate
    if (isActive) {
      return true;
    }

    // check if any active work order on the tool
    const hasActiveWorkOrders = await lastValueFrom(
      this.toolService.hasActiveWorkOrders(this.companyId, this.serialNumber),
    );

    if (hasActiveWorkOrders) {
      this.modalService.openConfirmationModal(
        new ConfirmationDialogData(
          LocalizationConstants.TOOL_ACTIVATE_VALIDATION_TITLE,
          [LocalizationConstants.TOOL_ACTIVATE_VALIDATION_CONTENT],
          true,
        ),
      );

      // mark tool as active again
      this.tool.isActive = true;
      this.globalOverlayService.close();
      return false;
    }

    return true;
  }

  /**
   * On component loaded set toolDetails on child components
   */
  onOutletLoaded(component: Component & { toolDetails: ToolDetails }) {
    component.toolDetails = this.toolDetails;
  }

  goToServAid() {
    const lang = this.localizationService.currentLanguage;
    const servAidLink = this.brandingService.getServAidUrl();

    const brandName = this.brandingService.isAtlasCopcoBrand()
      ? this.brandingService.currentBrand
      : `${this.brandingService.currentBrand}1`; // for Desoutter they add  `1` intentionally. Subject to change in the future.

    const url = `${servAidLink}${lang.code.toLowerCase()}/${brandName}/Catalogue/${
      this.tool.articleNumber
    }/Identity`;

    window.open(url, "_blank");
  }

  ngOnDestroy() {
    this._subscriptions$.forEach((a) => a.unsubscribe());
  }
}
